import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import './App.css';
import Landing from './components/landing/Landing';
import LandingSplitScreen from './components/landing/LandingSplitScreen'; // SPLIT-SCREEN VERSION
import LandingTopCarousel from './components/landing/LandingTopCarousel'; // TOP-CAROUSEL VERSION (CAMBER-STYLE)
import AuthForm from './components/auth/AuthForm';
import IDCardDashboard from './components/IDCardDashboard';
import DailyClaimBanner from './components/DailyClaimBanner';
import TrainingBanner from './components/TrainingBanner';
import WelcomeScreen from './components/landing/WelcomeScreen';
import Header from './components/shared/Header';
import CreateWizard from './components/wizard/CreateWizard';
import Credits from './components/CreditsPageSNAP';
import AdminPanel from './components/admin/AdminPanel';
import LumaQuickMode from './components/generation/LumaQuickMode';  // NEU: Luma Quick Mode!
import VehicleDashboard from './components/vehicle/VehicleDashboard';  // NEU: Vehicle System!
import ProtectedRoute from './components/auth/ProtectedRoute';
import SecuritySettings from './components/auth/SecuritySettings';  // NEU: Security Settings
import MaintenanceMode from './components/MaintenanceMode';  // NEU: Wartungsmodus
import { API_URL } from './config';  // NEU: API URL
import { ConsentProvider } from './context/ConsentContext';
import CookieBanner from './components/CookieBanner';
import CookiePreferencesModal from './components/modals/CookiePreferencesModal';
import Datenschutz from './components/pages/Datenschutz';
import Impressum from './components/pages/Impressum';
import AGB from './components/pages/AGB';
import Kontakt from './components/pages/Kontakt';
import WasIstSnapshot from './components/pages/WasIstSnapshot';
import GeneratorTest from './components/pages/GeneratorTest';
import VerifyEmailPage from './components/pages/VerifyEmailPage';
// import SnapshotHelpBot from './components/SnapshotHelpBot';  // DEAKTIVIERT: Nur Julia Voice Assistant aktiv
import { apiClient, rehydrateApiCache } from './utils/apiClient';  // NEU

// ETag/IDB-Cache beim App-Start rehydrieren (einmalig, non-blocking)
rehydrateApiCache();
import SkipPage from './components/landing/SkipPage';  // NEU: Skip-Seite mit Showcase
import useVersionCheck from './hooks/useVersionCheck';  // NEU: Auto-Refresh bei neuer Version
import SeoMetaInjector from './components/SeoMetaInjector';  // NEU: SEO Meta Tag Injection

// Version Checker Komponente - rendert nichts, checkt nur Version
function VersionChecker() {
  useVersionCheck();
  return null;
}

// Referral Redirect Component - verhindert Doppel-Tracking durch React Renders
function ReferralRedirect() {
  const [shouldRedirect, setShouldRedirect] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const ref = urlParams.get('ref');

    if (ref) {
      // Speichere in localStorage
      localStorage.setItem('snapshot_referral_code', ref);

      // Deduplizierung: Prüfe ob dieser Code innerhalb der letzten Sekunde bereits getrackt wurde
      const lastTracked = sessionStorage.getItem('last_tracked_ref');
      const lastTrackedTime = sessionStorage.getItem('last_tracked_time');
      const now = Date.now();

      const isDuplicate = lastTracked === ref && lastTrackedTime && (now - parseInt(lastTrackedTime)) < 1000;

      if (!isDuplicate) {
        // Track Link-Klick (async)
        fetch(`${API_URL}/api/referral/track-invite?referral_code=${ref}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        }).catch(err => console.log('Track-invite failed:', err));

        // Speichere für Deduplizierung
        sessionStorage.setItem('last_tracked_ref', ref);
        sessionStorage.setItem('last_tracked_time', now.toString());
      }
    }

    // Redirect nach kurzem Delay
    setShouldRedirect(true);
  }, []);

  return shouldRedirect ? <Navigate to="/" replace /> : null;
}

// 🔄 TOGGLE: Wechsel zwischen Landing-Versionen
// 'original' = Original Landing Page
// 'split' = Split-Screen (Desktop: Side-by-Side)
// 'top-carousel' = Top Carousel (Camber-Style, Auto-Play oben)
const LANDING_VERSION = 'original';

// NavigateSetter: Macht useNavigate() außerhalb des Routers verfügbar (via Ref)
function NavigateSetter({ navigateRef }) {
  const nav = useNavigate();
  useEffect(() => { navigateRef.current = nav; }, [nav, navigateRef]);
  return null;
}

function App() {
  const [credits, setCredits] = useState(0);
  const [currentUser, setCurrentUser] = useState(null);
  const [showCookieSettings, setShowCookieSettings] = useState(false);
  const [hasAvatar, setHasAvatar] = useState(false);
  const [checkingAvatar, setCheckingAvatar] = useState(true);
  const [showMaintenance, setShowMaintenance] = useState(false);  // NEU: Wartungsmodus
  // Beta-Gate ENTFERNT - Security-Fix

  // Ref für navigate() — wird von NavigateSetter innerhalb des Routers gesetzt
  const navigateRef = useRef(null);

  // Check if user is logged in. Impersonation-Übergabe MUSS hier passieren —
  // SYNCHRON bevor irgendeine Route gerendert wird. Sonst macht <Navigate>
  // im Welcome-Route die URL leer (Query-Param weg) und der Impersonation-Key
  // ist verloren, bevor ein useEffect ihn lesen könnte.
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const impKey = params.get('impersonate');
      if (impKey) {
        const raw = localStorage.getItem(impKey) || sessionStorage.getItem(impKey);
        if (raw) {
          localStorage.removeItem(impKey);
          sessionStorage.removeItem(impKey);
          const { token, user, adminToken, adminUser } = JSON.parse(raw);
          sessionStorage.setItem('snapshot_token', token);
          sessionStorage.setItem('snapshot_user', JSON.stringify(user));
          sessionStorage.setItem('snapshot_impersonation', 'true');
          sessionStorage.setItem('snapshot_impersonate_email', user.email || '');
          if (adminToken) sessionStorage.setItem('snapshot_admin_token', adminToken);
          if (adminUser) sessionStorage.setItem('snapshot_admin_user', adminUser);
          // URL aufräumen
          window.history.replaceState({}, '', '/dashboard');
          return true;
        }
      }
    } catch (e) {
      console.error('Impersonation init failed:', e);
    }
    const token = sessionStorage.getItem('snapshot_token') || localStorage.getItem('snapshot_token');
    const user = sessionStorage.getItem('snapshot_user') || localStorage.getItem('snapshot_user');
    return !!(token && user);
  });

  // currentUser auch initial setzen — damit das erste Render direkt den richtigen User hat
  useEffect(() => {
    if (sessionStorage.getItem('snapshot_impersonation') === 'true') {
      try {
        const u = JSON.parse(sessionStorage.getItem('snapshot_user') || 'null');
        if (u) setCurrentUser(u);
      } catch (e) {}
    }
  }, []);

  // Cookie Settings Handler
  useEffect(() => {
    const handleOpenSettings = () => setShowCookieSettings(true);
    window.addEventListener('openCookieSettings', handleOpenSettings);
    return () => window.removeEventListener('openCookieSettings', handleOpenSettings);
  }, []);

  // Wartungsmodus-Check
  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const res = await fetch(`${API_URL}/api/system/status`);

        // Validate response is JSON
        const contentType = res.headers.get("content-type");
        if (!res.ok || !contentType || !contentType.includes("application/json")) {
          // Silently fail or log warning instead of crashing on HTML response
          return;
        }

        const data = await res.json();

        if (data.maintenance_mode) {
          const user = sessionStorage.getItem('snapshot_user') || localStorage.getItem('snapshot_user');
          const parsed = user ? JSON.parse(user) : null;
          const isAdmin = parsed && parsed.is_admin === true;

          if (!isAdmin) {
            setShowMaintenance(true);
          }
        }
      } catch (error) {
        console.error('Wartungsmodus-Check fehlgeschlagen:', error);
      }
    };

    checkMaintenance();
  }, []);

  // Load user on mount if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const userData = sessionStorage.getItem('snapshot_user') || localStorage.getItem('snapshot_user');
      if (userData) {
        try {
          const user = JSON.parse(userData);

          // Hole aktuelle User-Daten von API (inkl. profile_image_url)
          const token = sessionStorage.getItem('snapshot_token') || localStorage.getItem('snapshot_token');
          if (token) {
            (async () => {
              try {
                const res = await fetch(`${API_URL}/api/user/me`, {
                  headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                  const apiUser = await res.json();
                  // Merge API-Daten mit gespeicherten User-Daten
                  user.profile_image_url = apiUser.profile_image_url;
                  user.free_images = apiUser.free_images;
                  user.free_video_credits = apiUser.free_video_credits;
                  user.credits = apiUser.credits;
                  user.is_admin = apiUser.is_admin;
                  user.onboarding_completed = apiUser.onboarding_completed;
                  // Bei Impersonation: nur sessionStorage (sonst überschreiben wir den Admin-User in localStorage)
                  const isImp = sessionStorage.getItem('snapshot_impersonation') === 'true';
                  (isImp ? sessionStorage : localStorage).setItem('snapshot_user', JSON.stringify(user));
                  setCurrentUser(user);
                }
              } catch (err) {
                console.error('Fehler beim Laden der User-Daten:', err);
              }
            })();
          } else {
            setCurrentUser(user);
          }

          // SIMPLIFIED: Always show Dashboard/ID Card
          checkUserAvatar(user.id);
        } catch (error) {
          console.error('Error parsing user data:', error);
          handleLogout();
        }
      }
    } else {
      setCheckingAvatar(false);
    }
  }, [isAuthenticated]);

  // Tab-Visibility: Token bei Rückkehr prüfen + Heartbeat senden
  // Safari: Intervals werden in Background-Tabs gestoppt, deshalb Heartbeat auch hier
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;
      const token = localStorage.getItem('snapshot_token');
      if (!token) return;
      try {
        const res = await fetch(`${API_URL}/api/user/me`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.status === 401) {
          // Doppel-Check: Nochmal versuchen bevor wir ausloggen (Safari kann temporäre 401s produzieren)
          await new Promise(r => setTimeout(r, 1000));
          const retry = await fetch(`${API_URL}/api/user/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (retry.status === 401) {
            handleLogout();
          }
        }
      } catch (err) {
        // Netzwerkfehler → NICHT ausloggen, User könnte offline sein
        console.debug('Visibility-Check fehlgeschlagen:', err);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated]);

  // Heartbeat: Update last_activity — mit Safari-Fallback
  useEffect(() => {
    if (!isAuthenticated) return;

    const sendHeartbeat = async () => {
      try {
        const token = localStorage.getItem('snapshot_token');
        if (!token) return;
        await fetch(`${API_URL}/api/auth/heartbeat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ token }),
          keepalive: true  // Safari: Request überlebt Tab-Suspension
        });
      } catch (error) {
        // Ignorieren — Heartbeat ist nicht kritisch
      }
    };

    sendHeartbeat();
    const interval = setInterval(sendHeartbeat, 2 * 60 * 1000);

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Check if user has an avatar
  const checkUserAvatar = async (userId) => {
    try {
      const res = await apiClient.get('/api/training/list');

      // Handle non-OK responses before reading body
      if (!res.ok) {
        if (res.status === 404) {
          // New user with no training jobs - this is normal
          setHasAvatar(false);
          return;
        }
        throw new Error(`API returned ${res.status}`);
      }

      const data = await res.json();
      const jobs = data.jobs || [];  // Backend returns data.jobs, not data.training_jobs
      // User has avatar if there's at least one training job with status 'ready'
      const hasReadyAvatar = jobs.some(job => job.status === 'ready');
      setHasAvatar(hasReadyAvatar);
    } catch (error) {
      console.error('Error checking avatar:', error);
      setHasAvatar(false);
    } finally {
      setCheckingAvatar(false);
    }
  };

  const handleEnter = (mode) => {
    // Navigiere zu /login oder /register statt State zu setzen
    if (navigateRef.current) {
      navigateRef.current(mode === 'register' ? '/register' : '/login');
    }
  };

  const handleAuthSuccess = (user, isNewRegistration = false) => {
    setCurrentUser(user);
    setIsAuthenticated(true);
    setCheckingAvatar(true);

    // Check if user has avatar (for UI display)
    checkUserAvatar(user.id);

    // Navigiere zum Dashboard
    if (navigateRef.current) {
      navigateRef.current('/dashboard', { replace: true });
    }
  };

  const handleAuthBack = () => {
    if (navigateRef.current) {
      navigateRef.current('/welcome');
    }
  };

  const handleLogout = () => {
    // Impersonation-Tab? → Nur sessionStorage leeren, Tab schließen
    if (sessionStorage.getItem('snapshot_impersonation')) {
      sessionStorage.clear();
      window.close();
      return;
    }
    localStorage.removeItem('snapshot_token');
    localStorage.removeItem('snapshot_user');
    localStorage.removeItem('snapshot_beta_access');
    sessionStorage.removeItem('snapshot_welcome_shown');
    setCurrentUser(null);
    setIsAuthenticated(false);
    window.location.href = '/welcome';
  };

  const handleStartCreation = () => {
    if (navigateRef.current) {
      navigateRef.current('/wizard');
    }
  };

  const handleBackToDashboard = () => {
    if (navigateRef.current) {
      navigateRef.current('/dashboard');
    }
  };

  return (
    <ConsentProvider>
      {/* Wartungsmodus - Admin-Bypass */}
      {showMaintenance && <MaintenanceMode />}

      {!showMaintenance && (
        <Router>
          <NavigateSetter navigateRef={navigateRef} />
          <VersionChecker /> {/* Auto-Refresh bei neuer App-Version */}
          <SeoMetaInjector /> {/* SEO Meta Tags vom Backend laden */}
          <Routes>
            {/* Admin Routes - No Beta Gate */}
            <Route path="/admin/*" element={(() => {
              try {
                const u = JSON.parse(localStorage.getItem('snapshot_user') || '{}');
                if (u.is_admin === true || u.email === 'info@snapshotpro.ai') {
                  return <AdminPanel />;
                }
              } catch (e) {}
              return <Navigate to="/" replace />;
            })()} />

            {/* Auth Route - Referral-Links: Track Klick, speichere ref, redirect */}
            <Route path="/auth" element={<ReferralRedirect />} />

            {/* Skip Page - Julia Showcase (NUR für nicht-angemeldete User) */}
            <Route path="/" element={
              isAuthenticated ? <Navigate to="/dashboard" replace /> : <SkipPage />
            } />

            {/* Welcome/Landing - für nicht-angemeldete User */}
            <Route path="/welcome" element={
              isAuthenticated ? <Navigate to="/dashboard" replace /> : (
                LANDING_VERSION === 'top-carousel' ? (
                  <LandingTopCarousel onEnter={handleEnter} />
                ) : LANDING_VERSION === 'split' ? (
                  <LandingSplitScreen onEnter={handleEnter} />
                ) : (
                  <Landing onEnter={handleEnter} />
                )
              )
            } />

            {/* Login / Register - eigene Routes für Back/Forward */}
            <Route path="/login" element={
              isAuthenticated ? <Navigate to="/dashboard" replace /> : (
                <AuthForm mode="login" onSuccess={handleAuthSuccess} onBack={handleAuthBack} />
              )
            } />
            <Route path="/register" element={
              isAuthenticated ? <Navigate to="/dashboard" replace /> : (
                <AuthForm mode="register" onSuccess={handleAuthSuccess} onBack={handleAuthBack} />
              )
            } />

            {/* Double-Opt-In: Landing für den Link aus der Verifikations-Mail */}
            <Route path="/verify-email" element={
              <VerifyEmailPage onSuccess={handleAuthSuccess} />
            } />

            {/* Dashboard - Hauptansicht für eingeloggte User */}
            <Route path="/dashboard" element={
              !isAuthenticated ? <Navigate to="/welcome" replace /> : (
                checkingAvatar ? (
                  <div className="min-h-screen bg-black grain-texture flex items-center justify-center">
                    <div className="w-16 h-16 border-4 border-[#10B981] border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : (
                  <>
                    <IDCardDashboard
                      onStartCreation={handleStartCreation}
                      onBackToDashboard={handleBackToDashboard}
                      currentUser={currentUser}
                      onLogout={handleLogout}
                    />
                    <DailyClaimBanner />
                    <TrainingBanner />
                  </>
                )
              )
            } />

            {/* Legal Pages */}
            <Route path="/datenschutz" element={<Datenschutz />} />
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/agb" element={<AGB />} />
            <Route path="/kontakt" element={<Kontakt />} />
            <Route path="/was-ist-snapshot" element={<WasIstSnapshot />} />

            {/* Luma Quick Mode - NUR für Admin! */}
            <Route path="/quick-mode" element={<LumaQuickMode currentUser={currentUser} onLogout={handleLogout} />} />
            <Route path="/vehicles" element={<VehicleDashboard currentUser={currentUser} onLogout={handleLogout} />} />

            {/* PROTECTED ROUTES - Require Authentication */}
            <Route path="/settings/security" element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <SecuritySettings />
              </ProtectedRoute>
            } />
            <Route path="/credits" element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <Credits />
              </ProtectedRoute>
            } />
            <Route path="/wizard" element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <CreateWizard onBackToDashboard={handleBackToDashboard} />
              </ProtectedRoute>
            } />

            <Route path="/generator-test" element={<GeneratorTest />} />

            {/* Fallback: Unbekannte Pfade → Startseite */}
            <Route path="*" element={<Navigate to={isAuthenticated ? '/dashboard' : '/'} replace />} />
          </Routes>

          {/* Cookie Banner & Settings */}
          <CookieBanner />
          {showCookieSettings && (
            <CookiePreferencesModal onClose={() => setShowCookieSettings(false)} />
          )}
        </Router>
      )}
    </ConsentProvider>
  );
}

export default App;
